/**
 * A ~40-line DOM builder. The widget deliberately ships no framework: it runs
 * on someone else's page, so every kilobyte is rent we charge our customers'
 * visitors.
 */

type Child = Node | string | number | false | null | undefined

export interface Props {
  class?: string
  style?: string
  text?: string
  html?: string
  [key: string]: unknown
}

function apply(el: Element, props: Props): void {
  for (const [key, value] of Object.entries(props)) {
    if (value === undefined || value === null || value === false) continue

    if (key === 'text') {
      el.textContent = String(value)
    } else if (key === 'html') {
      el.innerHTML = String(value)
    } else if (key.startsWith('on') && typeof value === 'function') {
      el.addEventListener(key.slice(2).toLowerCase(), value as EventListener)
    } else if (key === 'class') {
      el.setAttribute('class', String(value))
    } else {
      el.setAttribute(key, String(value))
    }
  }
}

function append(el: Element, children: Child[]): void {
  for (const child of children) {
    if (child === null || child === undefined || child === false) continue
    el.appendChild(typeof child === 'object' ? child : document.createTextNode(String(child)))
  }
}

export function h(tag: string, props: Props = {}, ...children: Child[]): HTMLElement {
  const el = document.createElement(tag)
  apply(el, props)
  append(el, children)
  return el
}

const SVG_NS = 'http://www.w3.org/2000/svg'

export function svg(tag: string, props: Props = {}, ...children: Child[]): SVGElement {
  const el = document.createElementNS(SVG_NS, tag)
  apply(el, props)
  append(el, children)
  return el
}

/** Replaces a container's children in one shot. */
export function replace(container: Element, ...children: Child[]): void {
  container.textContent = ''
  append(container, children)
}

/** Inline icons, matching the prototype's toolbar exactly. */
export const icons = {
  pin: `<svg width="16" height="16" viewBox="0 0 18 18" aria-hidden="true"><circle cx="9" cy="7" r="4.5" fill="none" stroke="currentColor" stroke-width="1.8"/><line x1="9" y1="11.5" x2="9" y2="16" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>`,
  rect: `<svg width="16" height="16" viewBox="0 0 18 18" aria-hidden="true"><rect x="2.5" y="4" width="13" height="10" rx="2" fill="none" stroke="currentColor" stroke-width="1.8"/></svg>`,
  draw: `<svg width="16" height="16" viewBox="0 0 18 18" aria-hidden="true"><path d="M2.5 13.5 C 5 7, 8.5 14.5, 15.5 3.5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>`,
  // The SpotNote shuriken, same geometry as the app's Mark (components/Brand.tsx).
  star: `<svg width="16" height="16" viewBox="0 0 120 120" aria-hidden="true"><g transform="translate(60 60)" fill="#e5484d"><rect x="-11" y="-52" width="22" height="104" rx="11"/><rect x="-11" y="-52" width="22" height="104" rx="11" transform="rotate(60)"/><rect x="-11" y="-52" width="22" height="104" rx="11" transform="rotate(120)"/></g></svg>`,
  starLarge: `<svg width="30" height="30" viewBox="0 0 120 120" aria-hidden="true"><g transform="translate(60 60)" fill="#e5484d"><rect x="-11" y="-52" width="22" height="104" rx="11"/><rect x="-11" y="-52" width="22" height="104" rx="11" transform="rotate(60)"/><rect x="-11" y="-52" width="22" height="104" rx="11" transform="rotate(120)"/></g></svg>`,
  check: `<svg width="56" height="56" viewBox="0 0 56 56" aria-hidden="true"><circle cx="28" cy="28" r="26" fill="#f0fdf4" stroke="#22c55e" stroke-width="2"/><path d="M18 29 L25 36 L38 21" fill="none" stroke="#16a34a" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
  globe: `<svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" aria-hidden="true"><circle cx="8" cy="8" r="6.3"/><ellipse cx="8" cy="8" rx="2.8" ry="6.3"/><path d="M1.7 8 h12.6"/></svg>`,
}
